import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import NewsletterCTA from "../components/NewsletterCTA";
import { useSeo } from "../lib/useSeo";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  Clock,
  Compass,
  Globe,
  Mail,
  Minus,
  Plus,
  ShieldCheck,
  Target,
  Train,
  Users,
} from "lucide-react";

const HERO_IMG = "/Traininplatform.jpg";
const SITE_URL = "https://railwaycareers.co.uk";

const PRINCIPLES = [
  {
    icon: Train,
    title: "Rail-only focus",
    body:
      "Railway Careers exists for people looking specifically at UK rail careers. The platform avoids the noise of generalist job boards and keeps the focus on rail operators, depots, vacancies and career pathways.",
  },
  {
    icon: Compass,
    title: "Practical applicant guidance",
    body:
      "Rail recruitment can be difficult to navigate. We aim to make vacancies, employer links, location considerations and application expectations easier to understand before people apply.",
  },
  {
    icon: ShieldCheck,
    title: "Safety-critical realism",
    body:
      "Railway careers are not just ordinary jobs. Many roles involve strict procedures, safety responsibility, shift work, medical standards and competence expectations. Our content is written with that reality in mind.",
  },
  {
    icon: BadgeCheck,
    title: "Clear routes to employers",
    body:
      "Where possible, Railway Careers directs applicants back to the original employer, operator or official application source so users can verify requirements and apply through the correct route.",
  },
];

const AUDIENCES = [
  "People applying for their first railway role",
  "Career changers exploring rail as a long-term profession",
  "Service leavers comparing transferable skills with rail careers",
  "Applicants looking for realistic commute and depot information",
  "Students and apprentices considering railway career pathways",
  "Experienced rail staff looking for new opportunities",
];

const DIFFERENTIATORS = [
  {
    k: "Built for rail applicants",
    v: "The site is structured around how people actually search for rail work: role, location, employer, commute and application timing.",
  },
  {
    k: "UK railway context",
    v: "The content is written for the UK rail employment market, including train operators, infrastructure employers, contractors and safety-critical recruitment expectations.",
  },
  {
    k: "Independent and applicant-led",
    v: "Railway Careers is designed to help users make informed decisions. It is not an official operator website and does not replace individual employer guidance.",
  },
];

const FAQS = [
  {
    q: "What is Railway Careers UK?",
    a:
      "Railway Careers UK is an independent rail careers platform built to help people explore UK railway jobs, understand rail career pathways and find useful employer, vacancy and location information in one place.",
  },
  {
    q: "Is Railway Careers an official Train Operating Company website?",
    a:
      "No. Railway Careers is independent. It is not an official Train Operating Company, Network Rail or employer website. Users should always check the original employer vacancy page before applying.",
  },
  {
    q: "Who is Railway Careers for?",
    a:
      "The platform is for anyone exploring UK rail careers, including aspiring train drivers, conductors, customer service applicants, engineering candidates, apprentices, graduates, service leavers and career changers.",
  },
  {
    q: "Does Railway Careers guarantee a rail job?",
    a:
      "No. Railway Careers does not guarantee employment, assessment success or interview success. The aim is to make rail career information easier to understand and help applicants make better-informed decisions.",
  },
  {
    q: "How should applicants use the site?",
    a:
      "Applicants should use Railway Careers to research railway roles, understand employer expectations, check realistic locations and follow links to official employer application pages where available.",
  },
  {
    q: "Why does rail recruitment need a specialist platform?",
    a:
      "Rail recruitment often involves safety-critical standards, shift work, location restrictions, medical requirements, assessment stages and strict application processes. A rail-specific platform can explain those issues more clearly than a generic job board.",
  },
];

const FADE = {
  initial: { opacity: 0, y: 14 },
  whileInView: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
  viewport: { once: true, margin: "-80px" },
};

function FAQItem({ faq, idx }) {
  const [open, setOpen] = useState(idx === 0);

  return (
    <div className="border-b border-line">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
        data-testid={`faq-${idx}`}
        aria-expanded={open}
      >
        <div className="font-display text-ice text-lg md:text-xl tracking-[-0.018em] font-semibold">
          {faq.q}
        </div>

        <div className="w-9 h-9 rounded-full border border-line group-hover:border-sky flex items-center justify-center text-ice transition-colors shrink-0">
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </div>
      </button>

      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-slate leading-relaxed max-w-3xl">{faq.a}</p>
      </motion.div>
    </div>
  );
}

export default function About() {
  useSeo({
    title: "About Railway Careers UK | Independent UK Rail Careers Platform",
    description:
      "Learn about Railway Careers UK, an independent rail careers platform helping applicants understand UK railway jobs, employers, locations and application routes.",
    canonical: "/about",
    image: "/og-home.jpg",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Railway Careers UK",
      description:
        "Independent UK railway careers platform helping applicants understand rail jobs, employers, locations and career pathways.",
      url: `${SITE_URL}/about`,
      isPartOf: {
        "@type": "WebSite",
        name: "Railway Careers UK",
        url: SITE_URL,
      },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: `${SITE_URL}/og-home.jpg`,
      },
    },
  });

  const organisationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Railway Careers UK",
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/logo.png`,
    description:
      "Independent UK railway careers platform helping applicants understand rail vacancies, employers, locations and career pathways.",
    areaServed: {
      "@type": "Country",
      name: "United Kingdom",
    },
    knowsAbout: [
      "UK rail careers",
      "Railway recruitment",
      "Train Operating Companies",
      "Rail apprenticeships",
      "Train driver careers",
      "Railway engineering careers",
      "Rail depot locations",
      "Rail job applications",
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <div className="bg-nav">
      <section className="relative overflow-hidden mesh-bg">
        <div className="absolute inset-0 grid-bg pointer-events-none opacity-50" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-sky/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 lg:pt-24 pb-20">
          <motion.div {...FADE} className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="chapter-mark">About Railway Careers UK</div>

              <h1 className="mt-8 font-display text-ice text-[44px] sm:text-6xl lg:text-[78px] leading-[0.98] tracking-[-0.032em] font-extrabold">
                A clearer way to understand{" "}
                <span className="gradient-text">UK railway careers.</span>
              </h1>

              <p className="mt-7 text-slate text-lg md:text-xl leading-[1.6] max-w-3xl">
                Railway Careers UK is an independent rail careers platform built to help
                applicants understand railway roles, employer routes, location factors and
                the practical realities of working in the UK rail industry.
              </p>

              <p className="mt-5 text-slate leading-[1.75] max-w-3xl">
                The site brings rail-specific information together in a more structured way,
                so applicants can research opportunities with better context before deciding
                where and when to apply.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/vacancies" className="pill pill-primary" data-testid="about-vacancies-cta">
                  Search rail vacancies <ArrowRight className="w-4 h-4" />
                </Link>

                <Link to="/depot-finder" className="pill pill-ghost" data-testid="about-depot-cta">
                  Check depots near you
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-line">
                <img
                  src={HERO_IMG}
                  alt="Railway station platform representing UK railway careers"
                  className="w-full h-[360px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-nav via-nav/35 to-transparent" />

                <div className="absolute left-0 right-0 bottom-0 p-6">
                  <div className="paper-card !rounded-xl p-5 backdrop-blur-md bg-surf/85">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-sky font-mono">
                      Independent · UK rail focused
                    </div>
                    <div className="mt-3 font-display text-ice text-2xl font-bold tracking-[-0.018em]">
                      Built around real applicant decisions.
                    </div>
                    <p className="mt-2 text-slate text-[13px] leading-relaxed">
                      Role suitability, location, shift work, application routes and employer
                      expectations all matter before an applicant clicks apply.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-deep border-y border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div {...FADE} className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="chapter-mark">Our Purpose</div>
              <h2 className="mt-6 font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.05]">
                Helping applicants make{" "}
                <span className="text-sky">better-informed rail career decisions.</span>
              </h2>
            </div>

            <div className="lg:col-span-7 lg:pt-4">
              <p className="text-slate text-lg leading-[1.7] max-w-3xl">
                Many rail applicants start with a job title but quickly discover that the
                reality is more complex: depots, travel time, safety-critical standards,
                shift patterns, application windows, medical expectations and assessment
                stages all shape whether a role is suitable.
              </p>

              <p className="mt-5 text-slate leading-[1.75] max-w-3xl">
                Railway Careers UK exists to reduce that confusion. The aim is not to replace
                official employer guidance, but to make the early research stage clearer,
                more practical and more specific to the railway.
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRINCIPLES.map((item, i) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="paper-card p-7"
                >
                  <div className="w-11 h-11 rounded-lg bg-sky/10 border border-sky/30 flex items-center justify-center text-sky">
                    <Icon className="w-5 h-5" />
                  </div>

                  <h3 className="mt-5 font-display text-ice text-xl font-bold tracking-[-0.012em]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-slate text-[14px] leading-relaxed">
                    {item.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-nav border-b border-line relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div {...FADE} className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="chapter-mark">Who We Support</div>

              <h2 className="mt-6 font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.05]">
                For people taking rail careers{" "}
                <span className="text-sky">seriously.</span>
              </h2>

              <p className="mt-6 text-slate text-lg leading-[1.7]">
                Railway Careers is designed for applicants who want to understand the rail
                industry before sending applications, not just search job titles in isolation.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="paper-card p-7 md:p-9">
                <div className="grid sm:grid-cols-2 gap-4">
                  {AUDIENCES.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-sky mt-0.5 shrink-0" />
                      <span className="text-slate leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-line">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                    <p className="text-slate leading-relaxed">
                      The common thread is intent. Whether someone is looking at traincrew,
                      stations, engineering, apprenticeships or support roles, the platform is
                      built for users who want practical context before applying.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-deep border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div {...FADE} className="text-center max-w-3xl mx-auto mb-14">
            <div className="chapter-mark mx-auto">What Makes Us Different</div>

            <h2 className="mt-6 font-display text-ice text-4xl md:text-5xl tracking-[-0.025em] font-extrabold leading-[1.05]">
              Not a generic job board with rail added on.
            </h2>

            <p className="mt-5 text-slate text-lg leading-[1.65]">
              Railway Careers is structured around the questions rail applicants actually
              need answered before they apply.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {DIFFERENTIATORS.map((item, i) => (
              <motion.div
                key={item.k}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="paper-card p-7"
              >
                <div className="text-sky font-mono text-[13px] tracking-[0.18em] font-semibold">
                  0{i + 1}
                </div>

                <h3 className="mt-5 font-display text-ice text-2xl font-bold tracking-[-0.018em]">
                  {item.k}
                </h3>

                <p className="mt-3 text-slate text-[14px] leading-relaxed">
                  {item.v}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-nav border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div {...FADE} className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="chapter-mark">Our Approach</div>

              <h2 className="mt-6 font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.05]">
                Clear information, practical context and{" "}
                <span className="text-sky">responsible guidance.</span>
              </h2>

              <p className="mt-7 text-slate text-lg leading-[1.7]">
                Railway recruitment can be competitive, and no website can remove the need
                for individual suitability, preparation, employer checks or formal assessment.
                Our role is to help applicants understand the landscape more clearly.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {[
                  "We avoid unrealistic promises.",
                  "We encourage users to verify official vacancy requirements.",
                  "We explain practical constraints such as commute and shift work.",
                  "We keep the focus on informed applications, not shortcuts.",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-sky mt-0.5 shrink-0" />
                    <span className="text-slate text-[14px] leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="paper-card p-8 md:p-10 dotted-bg">
                <div className="grid gap-5">
                  {[
                    {
                      icon: Target,
                      title: "Better research",
                      body:
                        "Applicants can understand what a role involves before deciding whether it is realistic for their circumstances.",
                    },
                    {
                      icon: Building2,
                      title: "Employer awareness",
                      body:
                        "The platform helps users identify the organisations involved in UK rail employment and where official application routes sit.",
                    },
                    {
                      icon: Clock,
                      title: "Application timing",
                      body:
                        "Rail vacancies can open and close quickly. Regular updates help users stay closer to the market.",
                    },
                    {
                      icon: Globe,
                      title: "Location realism",
                      body:
                        "Depot, region and commute considerations are central because many rail roles involve early starts, late finishes and safety-critical attendance requirements.",
                    },
                  ].map((item) => {
                    const Icon = item.icon;

                    return (
                      <div key={item.title} className="flex gap-4">
                        <div className="w-11 h-11 rounded-lg bg-sky/10 border border-sky/30 flex items-center justify-center text-sky shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>

                        <div>
                          <h3 className="font-display text-ice text-xl font-bold tracking-[-0.012em]">
                            {item.title}
                          </h3>
                          <p className="mt-1.5 text-slate text-[14px] leading-relaxed">
                            {item.body}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-deep border-b border-line relative overflow-hidden">
        <div className="absolute inset-0 mesh-bg opacity-50 pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-24 text-center">
          <motion.div {...FADE}>
            <div className="chapter-mark mx-auto">Stay Updated</div>

            <h2 className="mt-6 font-display text-ice text-4xl md:text-6xl tracking-[-0.025em] font-extrabold leading-[1.05]">
              Rail opportunities move quickly.{" "}
              <span className="text-sky">Keep yourself informed.</span>
            </h2>

            <p className="mt-6 text-slate text-lg leading-[1.65] max-w-3xl mx-auto">
              The Monday Briefing is designed for people who want a regular view of new
              rail vacancies, useful updates and practical application awareness without
              needing to check every employer site manually.
            </p>

            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Link to="/vacancies" className="pill pill-primary">
                Search current vacancies <ArrowRight className="w-4 h-4" />
              </Link>

              <a href="#newsletter" className="pill pill-ghost">
                Join the Monday Briefing <Mail className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-nav border-b border-line">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24">
          <motion.div {...FADE} className="text-center mb-12">
            <div className="chapter-mark mx-auto">About Railway Careers FAQ</div>

            <h2 className="mt-6 font-display text-ice text-4xl md:text-5xl tracking-[-0.025em] font-extrabold leading-[1.05]">
              Straight answers about{" "}
              <span className="text-sky">what this platform is.</span>
            </h2>
          </motion.div>

          <div>
            {FAQS.map((f, i) => (
              <FAQItem key={f.q} faq={f} idx={i} />
            ))}
          </div>
        </div>
      </section>

      <section id="newsletter" className="bg-deep py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <NewsletterCTA variant="dark" />
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationSchema) }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </div>
  );
}